import {
  parseCompanyListXml,
  parseGstNumberXml,
  parseVendorNamesXml,
  parseSingleVendorDetailsXml,
  parseActiveCompanyNameXml,
  parseVoucherTypesXml,
  parseVoucherRegistersXml,
  parsePurchaseAndSalesLedgersXml,
  escapeTdlString,
} from '../tally/tally-helpers';
import { TallyVendor } from '../../types/vendor';
import { TallyError } from '../errors/TallyError';
import posthog from 'posthog-js';

const TALLY_ENDPOINT = 'http://localhost:9000/tally'; // Default Tally endpoint

export const getTallyCompanies = async (): Promise<string[]> => {
  try {
    const xmlQuery = `
  <ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>ActiveCompanyReport</ID>
    </HEADER>
    <BODY>
        <DESC>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="ActiveCompanyReport">
                        <TYPE>Company</TYPE>
                        <FETCH>Name</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
  </ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseCompanyListXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyCompanies',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch companies from Tally',
    });
    throw new TallyError(
      'Failed to fetch companies from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getActiveTallyCompany = async (): Promise<string | null> => {
  try {
    const xmlQuery = `
  <ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>ActiveCompanyReport</ID>
    </HEADER>
    <BODY>
        <DESC>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="ActiveCompanyReport" ISMODIFY="No" ISFIXED="No" ISINITIALIZE="No" ISOPTION="No" ISINTERNAL="No">
                        <TYPE>Company</TYPE>
                        <FILTER>IsCurrentCompany</FILTER>
                        <FETCH>Name</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseActiveCompanyNameXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getActiveTallyCompany',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch active company from Tally',
    });
    throw new TallyError(
      'Failed to fetch active company from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallyCompanyDetails = async (
  companyName: string,
): Promise<string | null> => {
  try {
    const activeCompany = await getActiveTallyCompany();

    if (activeCompany !== companyName) {
      posthog.capture('tally_import_failure', {
        error_type: 'company_mismatch',
        function_name: 'getTallyCompanyDetails',
        message: `Active company mismatch: expected '${companyName}', got '${activeCompany}'`,
        expected_company: companyName,
        active_company: activeCompany,
      });
      throw new TallyError(
        `The active company in Tally is '${activeCompany}', but you are trying to fetch details for '${companyName}'. Please make the correct company active in Tally and try again.`,
      );
    }

    const xmlQuery = `
  <ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>ListOfTaxUnits</ID>
    </HEADER>
    <BODY>
        <DESC>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="ListOfTaxUnits">
                        <TYPE>Tax Unit</TYPE>
                        <FETCH>GSTRegNumber</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
  </ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseGstNumberXml(data);
  } catch (error) {
    if (error instanceof TallyError) {
      throw error;
    }
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyCompanyDetails',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch company details from Tally',
      company_name: companyName,
    });
    throw new TallyError(
      'Failed to fetch company details from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallySundryCreditors = async (): Promise<{
  cleanNames: string[];
  corruptedNames: string[];
}> => {
  try {
    const xmlQuery = `
<ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>Export</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>AllCreditorsOnly</ID>
    </HEADER>
    <BODY>
        <DESC>
            <STATICVARIABLES>
                <SVEXPORTFORMAT>$$SysName:XML</SVEXPORTFORMAT>
            </STATICVARIABLES>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="AllCreditorsOnly">
                        <TYPE>Ledger</TYPE>
                        <CHILDOF>$$GroupSundryCreditors</CHILDOF>
                        <BELONGSTO>Yes</BELONGSTO>
                        <FETCH>Name, Parent, LedgerStateName, CountryName, PinCode, PartyGSTIN, GSTRegistrationType, IncomeTaxNumber, Email, LedgerPhone, LedgerMobile, ClosingBalance, OpeningBalance</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseVendorNamesXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallySundryCreditors',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch sundry creditors from Tally',
    });
    throw new TallyError(
      'Failed to fetch sundry creditors from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallySundryDebtors = async (): Promise<{
  cleanNames: string[];
  corruptedNames: string[];
}> => {
  try {
    const xmlQuery = `
<ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>Export</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>AllDebtorsOnly</ID>
    </HEADER>
    <BODY>
        <DESC>
            <STATICVARIABLES>
                <SVEXPORTFORMAT>$$SysName:XML</SVEXPORTFORMAT>
            </STATICVARIABLES>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="AllDebtorsOnly">
                        <TYPE>Ledger</TYPE>
                        <CHILDOF>$$GroupSundryDebtors</CHILDOF>
                        <BELONGSTO>Yes</BELONGSTO>
                        <FETCH>Name, Parent, LedgerStateName, CountryName, PinCode, PartyGSTIN, GSTRegistrationType, IncomeTaxNumber, Email, LedgerPhone, LedgerMobile, ClosingBalance, OpeningBalance</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseVendorNamesXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallySundryDebtors',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch sundry debtors from Tally',
    });
    throw new TallyError(
      'Failed to fetch sundry debtors from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallyVendorNames = async (): Promise<{
  creditors: { cleanNames: string[]; corruptedNames: string[] };
  debtors: { cleanNames: string[]; corruptedNames: string[] };
}> => {
  try {
    // Fetch both creditors and debtors in parallel
    const [creditors, debtors] = await Promise.all([
      getTallySundryCreditors(),
      getTallySundryDebtors(),
    ]);

    return {
      creditors,
      debtors,
    };
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyVendorNames',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch vendor names from Tally',
    });
    throw new TallyError(
      'Failed to fetch vendor names from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

// New function to get specific vendor details by name
export const getTallyVendorDetailsByName = async (
  vendorName: string,
  companyName: string,
  isDebtor: boolean = false,
): Promise<TallyVendor | null> => {
  try {
    const xmlQuery = `
<ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>COLLECTION</TYPE>
        <ID>VendorGSTDetails_Final</ID>
    </HEADER>
    <BODY>
        <DESC>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="VendorGSTDetails_Final" ISMODIFY="No">
                        <TYPE>Ledger</TYPE>
                        <FETCH>Name, LEDGSTREGDETAILS.GSTIN</FETCH>
                        <FILTER>_VendorNameFilter</FILTER>
                    </COLLECTION>
                    <SYSTEM TYPE="FORMULA" NAME="_VendorNameFilter" ISMODIFY="No">$Name = ${escapeTdlString(vendorName)}</SYSTEM>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseSingleVendorDetailsXml(data, companyName, isDebtor);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyVendorDetailsByName',
      message:
        error instanceof Error
          ? error.message
          : `Failed to fetch details for vendor '${vendorName}'`,
      vendor_name: vendorName,
      company_name: companyName,
    });
    throw new TallyError(
      `Failed to fetch details for vendor '${vendorName}'. Please ensure Tally is running and accessible.`,
    );
  }
};

// Modified getTallyVendorDetails to orchestrate the two-step process
export const getTallyVendorDetails = async (
  companyName: string,
  onProgress: (progress: {
    percentage: number;
    message: string;
    processedVendors: number;
    totalVendors: number;
  }) => void,
): Promise<{ vendors: TallyVendor[]; corruptedVendors: string[] }> => {
  const currentSelectedCompany = await getActiveTallyCompany();
  if (currentSelectedCompany !== companyName) {
    posthog.capture('tally_import_failure', {
      error_type: 'company_mismatch',
      function_name: 'getTallyVendorDetails',
      message: `Active company mismatch: expected '${companyName}', got '${currentSelectedCompany}'`,
      expected_company: companyName,
      active_company: currentSelectedCompany,
    });
    throw new TallyError(
      `The active company in Tally is '${currentSelectedCompany}', but you are trying to fetch vendor details for '${companyName}'. Please make the correct company active in Tally and try again.`,
    );
  }

  const { creditors, debtors } = await getTallyVendorNames(); // Step 1: Get vendor names based on sundry creditors and debtors and it's sub-groups
  const allVendorDetails: TallyVendor[] = [];
  const allCorruptedNames = [
    ...creditors.corruptedNames,
    ...debtors.corruptedNames,
  ];

  // Combine vendors with their type (isDebtor flag)
  const allVendors = [
    ...creditors.cleanNames.map(name => ({ name, isDebtor: false })),
    ...debtors.cleanNames.map(name => ({ name, isDebtor: true })),
  ];
  const totalVendors = allVendors.length;

  for (let i = 0; i < totalVendors; i++) {
    const { name: vendorName, isDebtor } = allVendors[i];
    const progress = 10 + Math.round(((i + 1) / totalVendors) * 30);
    onProgress({
      percentage: progress,
      message: `Fetching details for vendor: ${vendorName}`,
      processedVendors: i + 1,
      totalVendors,
    });

    const details = await getTallyVendorDetailsByName(
      vendorName,
      companyName,
      isDebtor,
    );
    if (details) {
      allVendorDetails.push(details);
    }
  }
  return { vendors: allVendorDetails, corruptedVendors: allCorruptedNames };
};

export const getTallyVoucherTypes = async (): Promise<string[]> => {
  try {
    const xmlQuery = `
  <ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>Collection</TYPE>
        <ID>AllVoucherTypes</ID>
    </HEADER>
    <BODY>
        <DESC>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="AllVoucherTypes">
                        <TYPE>VoucherType</TYPE>
                        <FETCH>Name, Parent</FETCH>
                    </COLLECTION>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseVoucherTypesXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyVoucherTypes',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch voucher types from Tally',
    });
    throw new TallyError(
      'Failed to fetch voucher types from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallyPurchaseAndSalesLedgers = async (
  AccountName: string,
): Promise<any> => {
  try {
    const xmlQuery = `
<ENVELOPE>
    <HEADER>
        <VERSION>1</VERSION>
        <TALLYREQUEST>EXPORT</TALLYREQUEST>
        <TYPE>COLLECTION</TYPE>
        <ID>Groups</ID>
    </HEADER>
    <BODY>
        <DESC>
            <STATICVARIABLES>
                <SVEXPORTFORMAT>$SysName:XML</SVEXPORTFORMAT>
            </STATICVARIABLES>
            <TDL>
                <TDLMESSAGE>
                    <COLLECTION NAME="Groups" ISMODIFY="No">
                        <TYPE>Ledger</TYPE>
                        <FILTERS>PurchaseAccountFilter</FILTERS>
                        <FETCH>NAME,PARENT,GUID</FETCH>
                    </COLLECTION>
                    <SYSTEM TYPE="Formulae" NAME="PurchaseAccountFilter">
                        $Parent = "${AccountName}"
                    </SYSTEM>
                </TDLMESSAGE>
            </TDL>
        </DESC>
    </BODY>
</ENVELOPE>

  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parsePurchaseAndSalesLedgersXml(data);
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyPurchaseAndSalesLedgers',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch purchase and sales ledgers from Tally',
      account_name: AccountName,
    });
    throw new TallyError(
      'Failed to fetch purchase and sales ledgers from Tally. Please ensure Tally is running and accessible.',
    );
  }
};

export const getTallyVoucherRegisters = async (
  voucherType: string,
  purchaseAccountsLedgers: string[],
  salesAccountsLedgers: string[],
  directExpensesAccountsLedgers: string[],
  indirectExpensesAccountsLedgers: string[],
  indirectIncomesAccountsLedgers: string[],
  directIncomesAccountsLedgers: string[],
  journalAccountsLedgers: string[],
  fromDate: string,
  toDate: string,
): Promise<any> => {
  try {
    const xmlQuery = `
  <ENVELOPE>
    <HEADER>
        <TALLYREQUEST>Export Data</TALLYREQUEST>
    </HEADER>
    <BODY>
        <EXPORTDATA>
            <REQUESTDESC>
                <REPORTNAME>Voucher Register</REPORTNAME>
                <STATICVARIABLES>
                    <VOUCHERTYPENAME>${voucherType}</VOUCHERTYPENAME>
                    <SVFROMDATE>${fromDate}</SVFROMDATE>
                    <SVTODATE>${toDate}</SVTODATE>
                </STATICVARIABLES>
            </REQUESTDESC>
        </EXPORTDATA>
    </BODY>
</ENVELOPE>
  `;

    const response = await fetch(TALLY_ENDPOINT, {
      method: 'POST',
      body: xmlQuery,
    });

    const data = await response.text();
    return parseVoucherRegistersXml(
      data,
      voucherType,
      purchaseAccountsLedgers,
      salesAccountsLedgers,
      directExpensesAccountsLedgers,
      indirectExpensesAccountsLedgers,
      indirectIncomesAccountsLedgers,
      directIncomesAccountsLedgers,
      journalAccountsLedgers,
    );
  } catch (error) {
    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      function_name: 'getTallyVoucherRegisters',
      message:
        error instanceof Error
          ? error.message
          : `Failed to fetch voucher registers for voucher type '${voucherType}'`,
      voucher_type: voucherType,
      from_date: fromDate,
      to_date: toDate,
    });
    throw new TallyError(
      `Failed to fetch voucher registers for voucher type '${voucherType}'. Please ensure Tally is running and accessible.`,
    );
  }
};
